import { useForm } from "@mantine/form";
import { GraphQL, GraphQLQueryData, isGraphQLError } from "@rpi/openapi-api";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { createRpiDrawerView, RpiDrawerProps } from "../../../rpi-core/drawer/RpiDrawer";
import { shouldRetry } from "../../../utils/query.util";
import { validateCustomerSecretName } from "../../../utils/validation.util";
import { Group, Stack } from "@mantine/core";
import { RpaButtonLink } from "../../../components/RpiLink";
import { RpaDropdown } from "../../../components/inputFields/RpaDropdownInput";
import { RpaTextInput } from "../../../components/inputFields/RpaTextInput";
import { RpaButtonPromo } from "../../../components/buttons/RpaButton";
import { RpiAlert, useShowSuccessToast } from "@rpi/openapi-core";
import { RpaDrawer } from "../../../rpi-core/drawer/RpaDrawer";

export const CustomerEditTypeView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const { customerPublicId } = useParams();

    const [closing, setClosing] = React.useState(false);

    const handleClose = React.useCallback(
      (delay?: boolean) => {
        if (closing) return;

        setClosing(true);
        if (delay) {
          return setTimeout(onClose, 1000);
        }
        return onClose();
      },
      [onClose, closing]
    );

    const { data, isLoading, error } = GraphQL.useCustomerQuery(
      { customerPublicId: customerPublicId || "" },
      {
        enabled: !!customerPublicId && opened,
        refetchOnMount: true,
        retry: shouldRetry(handleClose),
        cacheTime: 0,
      }
    );
    const customer = data?.customer;

    return (
      <RpaDrawer title="Customer Type" isLoading={isLoading} error={error} opened={opened} onClose={handleClose}>
        {customer && <CustomerTypeComponent customer={customer} onClose={() => handleClose()} />}
      </RpaDrawer>
    );
  }
);

export enum CustomerOptions {
  GENERIC = "Generic (Self-Serve)",
  EXTERNAL = "Enterprise",
}

type EditUserForm = {
  customerType: GraphQL.CustomerType;
  customerSecretName?: string;
};

interface EditUserProps {
  customer: GraphQLQueryData<GraphQL.CustomerQuery>;
  onClose: () => void;
}

const CustomerTypeComponent: React.FC<EditUserProps> = ({ customer, onClose }) => {
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const showSuccessToast = useShowSuccessToast();

  const editCustomer = GraphQL.useAdminUpdateCustomerSecretAndSettingsMutation({
    onSuccess: () => {
      showSuccessToast("Customer type was updated successfully!");
      onClose();
    },
    onError: (error: unknown) => {
      if (isGraphQLError(error)) {
        if (error.data.message.includes("Secret not found")) {
          setErrorText("The provided secret name does not exist. Please enter a valid secret name.");
        } else if (error.data.message.includes("Invalid UOS credentials")) {
          setErrorText("The provided secret has invalid UOS credentials. Please update the credentials.");
        } else if (error.data.message.includes("Username, password, and customer name must be provided")) {
          setErrorText(
            "The provided secret is missing username, password, or customer name. Please update the secret."
          );
        } else {
          setErrorText("An unexpected error occurred. Please try again.");
        }
      } else {
        setErrorText("An unexpected error occurred. Please try again.");
      }
    },
  });

  const isLoading = editCustomer.isLoading;

  const form = useForm<EditUserForm>({
    initialValues: {
      customerType:
        customer.settings?.payments === "EXTERNAL" ? GraphQL.CustomerType.Enterprise : GraphQL.CustomerType.Generic,
      customerSecretName: customer.secretKey || "",
    },
    validate: {
      customerSecretName: (value: string) => {
        if (form.values.customerType === GraphQL.CustomerType.Enterprise) {
          const error = validateCustomerSecretName(value);
          if (error) {
            setErrorText(undefined);
          }
          return error;
        }
      },
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    editCustomer.mutate({
      customerPublicId: customer.publicId,
      customerType: values.customerType,
      secretKey: values.customerSecretName || "",
    });
  };

  const CUSTOMER_OPTIONS = [GraphQL.CustomerType.Enterprise, GraphQL.CustomerType.Generic].map((value) => ({
    label: value === GraphQL.CustomerType.Enterprise ? CustomerOptions.EXTERNAL : CustomerOptions.GENERIC,
    value,
  }));

  return (
    <>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack
          sx={(theme) => ({
            paddingLeft: `${theme.spacing.lg}px`,
            paddingRight: `${theme.spacing.lg}px`,
            marginBottom: `${theme.spacing.lg * 2}px`,
          })}
        >
          <RpaDropdown
            label="Customer Type"
            data={CUSTOMER_OPTIONS}
            placeholder="Select.."
            {...form.getInputProps("customerType")}
          />
          {form.values.customerType === GraphQL.CustomerType.Enterprise && (
            <>
              <RpaTextInput
                label="Customer Secret Name"
                placeholder="Enter"
                {...form.getInputProps("customerSecretName")}
              />
              {errorText && <RpiAlert>{errorText}</RpiAlert>}
            </>
          )}
        </Stack>
        <Stack
          sx={(theme) => ({
            paddingLeft: `${theme.spacing.lg}px`,
            paddingRight: `${theme.spacing.lg}px`,
            marginBottom: `${theme.spacing.md}px`,
          })}
        >
          <RpaButtonPromo type="submit" sx={{ height: "40px" }} fullWidth={true} disabled={isLoading}>
            Save Changes
          </RpaButtonPromo>
        </Stack>
      </form>
      <Group position="center">
        <RpaButtonLink onClick={() => onClose()}>Cancel</RpaButtonLink>
      </Group>
    </>
  );
};
